import React, { useState, useEffect, useContext, useCallback } from "react";
import {
  makeStyles,
  Typography,
  Avatar,
  Badge,
  List,
  ListItem,
  ListItemAvatar,
  ListItemText,
  Divider,
} from "@material-ui/core";

import api from "../../services/api";
import { getSocket } from "../../services/socket-io";
import { AuthContext } from "../../context/Auth/AuthContext";

const useStyles = makeStyles((theme) => ({
  root: {
    display: "flex",
    flexDirection: "column",
    height: "100%",
    overflow: "hidden",
  },
  header: {
    display: "flex",
    alignItems: "center",
    padding: theme.spacing(1, 2),
    borderBottom: `1px solid ${theme.palette.divider}`,
    backgroundColor: theme.palette.background.paper,
  },
  userList: {
    flex: 1,
    overflowY: "auto",
  },
  // feat_293: rojo de notificación (color="secondary", mismo que el bell/campana)
  // en vez del verde #25D366 poco visible. Texto blanco (contrastText MUI).
  badge: {
    "& .MuiBadge-badge": {
      fontWeight: 700,
    },
  },
}));

const InternalChatPanel = ({ onSelectUser }) => {
  const classes = useStyles();
  const { user } = useContext(AuthContext);
  const [users, setUsers] = useState([]);
  // Map { [counterpartUserId]: unreadCount }
  const [counts, setCounts] = useState({});
  // feat_294: Map { [counterpartUserId]: lastMessageAt(ms) } — recencia WhatsApp.
  const [lastActivity, setLastActivity] = useState({});

  // feat_294: última actividad (ambas direcciones) + no leídos, en un solo fetch.
  const fetchConversations = useCallback(() => {
    api
      .get("/internal-messages/conversations")
      .then(({ data }) => {
        const unreadMap = {};
        const activityMap = {};
        (data || []).forEach((c) => {
          if (c.unread) unreadMap[c.userId] = c.unread;
          activityMap[c.userId] = new Date(c.lastMessageAt).getTime();
        });
        setCounts(unreadMap);
        setLastActivity(activityMap);
      })
      .catch(() => {});
  }, []);

  useEffect(() => {
    api
      .get("/users")
      .then(({ data }) =>
        setUsers((data.users || data).filter((u) => u.id !== user?.id))
      )
      .catch(() => {});
    fetchConversations();
  }, [user?.id, fetchConversations]);

  // Live badge: increment on incoming direct message, zero on read-all.
  useEffect(() => {
    if (!user?.id) return;
    const socket = getSocket();

    const onMessage = (data) => {
      const msg = data.message || data;
      if (msg.toUserId === user.id && msg.fromUserId) {
        setCounts((prev) => ({
          ...prev,
          [msg.fromUserId]: (prev[msg.fromUserId] || 0) + 1,
        }));
        // feat_294: la conversación salta al tope en vivo (recencia WhatsApp).
        setLastActivity((prev) => ({
          ...prev,
          [msg.fromUserId]: Date.now(),
        }));
      }
    };
    const onRead = (data) => {
      const fromUserId = data?.fromUserId;
      if (!fromUserId) return;
      // Solo baja el badge; NO toca lastActivity → leer NO reordena.
      setCounts((prev) => {
        if (!prev[fromUserId]) return prev;
        const next = { ...prev };
        delete next[fromUserId];
        return next;
      });
    };

    socket.on("internalMessage", onMessage);
    socket.on("internalMessageRead", onRead);

    return () => {
      socket.off("internalMessage", onMessage);
      socket.off("internalMessageRead", onRead);
    };
  }, [user?.id]);

  // feat_294: orden WhatsApp — (1) con conversación por lastMessageAt DESC;
  // (2) sin conversación, debajo, alfabético. El unread NO afecta el orden.
  const sortedUsers = [...users].sort((a, b) => {
    const la = lastActivity[a.id] || 0;
    const lb = lastActivity[b.id] || 0;
    if (la !== lb) return lb - la; // más reciente arriba; sin conv (0) al fondo
    return (a.name || "").localeCompare(b.name || "");
  });

  return (
    <div className={classes.root}>
      <div className={classes.header}>
        <Typography variant="subtitle2" style={{ fontWeight: 600 }}>
          Chat interno — elige un usuario
        </Typography>
      </div>
      <List className={classes.userList}>
        {sortedUsers.map((u) => {
          const unread = counts[u.id] || 0;
          return (
            <React.Fragment key={u.id}>
              <ListItem button onClick={() => onSelectUser && onSelectUser(u)}>
                <ListItemAvatar>
                  <Badge
                    className={classes.badge}
                    color="secondary"
                    badgeContent={unread}
                    invisible={unread === 0}
                    overlap="circular"
                    anchorOrigin={{ vertical: "top", horizontal: "right" }}
                  >
                    <Avatar src={u.profilePicUrl}>{u.name?.[0]}</Avatar>
                  </Badge>
                </ListItemAvatar>
                <ListItemText
                  primary={u.name}
                  primaryTypographyProps={{
                    style: unread > 0 ? { fontWeight: 700 } : undefined,
                  }}
                  secondary={u.email}
                />
              </ListItem>
              <Divider component="li" />
            </React.Fragment>
          );
        })}
        {users.length === 0 && (
          <ListItem>
            <ListItemText secondary="No hay otros usuarios" />
          </ListItem>
        )}
      </List>
    </div>
  );
};

export default InternalChatPanel;
